import { useCallback, useEffect, useMemo } from 'react'

import { useLocation } from 'react-router-dom'

import { useTransactionsHistory } from 'state/user/hooks'
import { useAirdropId, useIsAirdropFailed, useSetAirdropStatus, useTxsList } from 'state/airdrop/hooks'

import { TxStatuses, TxTypes, AirdropStatus } from 'constants/transaction'

export function useAirdropTransactions() {
  const location = useLocation()

  const txsList = useTxsList()
  const airdropId = useAirdropId()

  const transactionsHistory = useTransactionsHistory()

  const isAirdropFailed = useIsAirdropFailed()
  const setAirdropStatus = useSetAirdropStatus()

  const transactions = useMemo(() => {
    return Object.values(transactionsHistory).filter(
      (tx) => tx.type !== TxTypes.TOP_UP_SELF_GENERATED && tx.type !== TxTypes.REFUND && tx.airdropId === airdropId,
    )
  }, [airdropId, transactionsHistory])

  const isMined = useMemo(() => {
    const sendTxs = transactions.filter((tx) => tx.status === TxStatuses.FAIL || tx.status === TxStatuses.SUCCESS)

    return (
      Boolean(airdropId) &&
      txsList.length === sendTxs.length &&
      txsList.length === transactions.length &&
      transactions.length !== 0
    )
  }, [transactions, airdropId, txsList])

  const updateAirdropStatus = useCallback(() => {
    const successTxs = transactions.filter((tx) => tx.status === TxStatuses.SUCCESS)

    if (isMined) {
      if (successTxs.length === transactions.length) {
        return setAirdropStatus(AirdropStatus.SUCCESS)
      } else {
        return setAirdropStatus(AirdropStatus.FAILED)
      }
    }

    if (!transactions.length) {
      return setAirdropStatus(AirdropStatus.NOT_STARTED)
    }

    if (transactions.some((tx) => tx.status === TxStatuses.PENDING)) {
      return setAirdropStatus(AirdropStatus.PENDING)
    }

    return setAirdropStatus(AirdropStatus.INTERRUPTED)
  }, [transactions, isMined])

  useEffect(() => {
    if (!isAirdropFailed) {
      updateAirdropStatus()
    }
  }, [transactions, isMined, location])

  return useMemo(() => ({ transactions, isMined }), [isMined, transactions])
}
